/**
 * @file components/audit-tasks/PhaseSteps.tsx
 * @description 稽核任務頂部 Steps 控件。
 *
 * 6 階段：文件審查 → 稽核計劃 → 實地稽核 → 檢驗 / 資材 → 報告編製 → 結案。
 *
 * 行為：
 *  - 點擊任一階段切換顯示內容（不嚴格約束順序，因為許多階段資料是並行存在的）
 *  - 當前階段（由後端推斷的 task.phase）以 process 樣式高亮
 *  - 已完成階段（在當前階段之前）顯示為 finish
 */

import { Steps } from 'antd';
import { AUDIT_TASK_PHASES, AUDIT_TASK_PHASE_LABELS, type AuditTaskPhase } from '@zhcb/shared';

interface Props {
  /** 後端推斷的當前階段（用於決定 finish/process 樣式邊界） */
  currentPhase: AuditTaskPhase;
  /** 使用者主動選擇的階段（控制顯示哪一塊內容） */
  selectedPhase: AuditTaskPhase;
  /** 本案已標記「無需檢驗」→ 檢驗步顯示「已跳過」 */
  labWaived?: boolean;
  onSelect: (phase: AuditTaskPhase) => void;
}

export function PhaseSteps({ currentPhase, selectedPhase, labWaived, onSelect }: Props) {
  const currentIndex = AUDIT_TASK_PHASES.indexOf(currentPhase);
  const selectedIndex = AUDIT_TASK_PHASES.indexOf(selectedPhase);

  return (
    <Steps
      type="navigation"
      size="small"
      current={selectedIndex}
      onChange={(idx) => {
        const next = AUDIT_TASK_PHASES[idx];
        if (next) onSelect(next);
      }}
      items={AUDIT_TASK_PHASES.map((p, idx) => ({
        title: AUDIT_TASK_PHASE_LABELS[p],
        // 序225：舊版曾在 planning/field/report 三步下方加「實地稽核」分組副標，
        // 但該詞是改名前的舊術語，疊在已改名的標題下方反而讓客戶覺得「名詞沒改對」。
        // 各階段標題本身已能清楚表意，不再需要這行分組副標；僅保留檢驗豁免的「已跳過」提示。
        description: p === 'lab' && labWaived ? '已跳過' : undefined,
        // current phase 之前 = finish，等於 = process，之後 = wait
        status:
          idx < currentIndex
            ? ('finish' as const)
            : idx === currentIndex
              ? ('process' as const)
              : ('wait' as const),
      }))}
    />
  );
}
